import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  BookOpen, Clock, Trophy, Lock, CheckCircle, XCircle, 
  ArrowRight, Star, Zap, Target, TrendingUp, Award,
  Play, BarChart3, Users, Timer, ChevronRight, Mail, Phone, Eye, EyeOff,
  MessageSquare
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Indian phone number validation
const isValidIndianPhone = (phone) => {
  const cleaned = phone.replace(/\D/g, '');
  // Indian phone: 10 digits, starts with 6, 7, 8, or 9
  return /^[6-9]\d{9}$/.test(cleaned);
};

// Email validation
const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

// Main Test Series Page
const TestSeries = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [plans, setPlans] = useState([]);
  const [userSubscription, setUserSubscription] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [testQuestions, setTestQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [testStarted, setTestStarted] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tests');
  const [leaderboard, setLeaderboard] = useState([]);
  const [myAttempts, setMyAttempts] = useState([]);
  
  // Auth states
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // login, signup, otp
  const [authForm, setAuthForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    otp: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Payment states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('test_series_user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      setIsLoggedIn(true);
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const userId = user?.id || localStorage.getItem('test_series_user_id');
      
      // Fetch tests
      const testsRes = await fetch(`${API_URL}/api/test-series/tests${userId ? `?user_id=${userId}` : ''}`);
      const testsData = await testsRes.json();
      if (testsData.success) {
        setTests(testsData.tests);
      }

      // Fetch plans
      const plansRes = await fetch(`${API_URL}/api/test-series/plans`);
      const plansData = await plansRes.json();
      if (plansData.success) {
        setPlans(plansData.plans);
      }

      // Fetch subscription if logged in
      if (userId) {
        const subRes = await fetch(`${API_URL}/api/test-series/subscription/${userId}`);
        const subData = await subRes.json();
        if (subData.success && subData.subscription) {
          setUserSubscription(subData.subscription);
        }

        // Fetch attempts
        const attemptsRes = await fetch(`${API_URL}/api/test-series/my-attempts/${userId}`);
        const attemptsData = await attemptsRes.json();
        if (attemptsData.success) {
          setMyAttempts(attemptsData.attempts);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  // Validate auth form
  const validateAuthForm = () => {
    const newErrors = {};
    
    if (authMode === 'signup' && !authForm.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!authForm.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(authForm.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!authForm.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidIndianPhone(authForm.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit Indian mobile number';
    }
    
    if (authMode !== 'otp' && !authForm.password) {
      newErrors.password = 'Password is required';
    } else if (authMode === 'signup' && authForm.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (authMode === 'otp' && otpSent && !authForm.otp) {
      newErrors.otp = 'Please enter the OTP';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle signup
  const handleSignup = async () => {
    if (!validateAuthForm()) return;
    
    setAuthLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/test-series/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: authForm.name,
          email: authForm.email,
          phone: authForm.phone,
          password: authForm.password
        })
      });
      const data = await res.json();
      
      if (data.success) {
        setUser(data.user);
        setIsLoggedIn(true);
        localStorage.setItem('test_series_user', JSON.stringify(data.user));
        localStorage.setItem('test_series_user_id', data.user.id);
        setShowAuthModal(false);
        toast({ title: "Welcome!", description: "Account created successfully" });
        fetchData();
      } else {
        toast({ title: "Error", description: data.detail || "Signup failed", variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Error", description: "Something went wrong", variant: "destructive" });
    }
    setAuthLoading(false);
  };

  // Handle login
  const handleLogin = async () => {
    if (!validateAuthForm()) return;
    
    setAuthLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/test-series/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: authForm.email,
          phone: authForm.phone,
          password: authForm.password
        })
      });
      const data = await res.json();
      
      if (data.success) {
        setUser(data.user);
        setIsLoggedIn(true);
        localStorage.setItem('test_series_user', JSON.stringify(data.user));
        localStorage.setItem('test_series_user_id', data.user.id);
        setShowAuthModal(false);
        toast({ title: "Welcome back!", description: "Logged in successfully" });
        fetchData();
      } else {
        toast({ title: "Error", description: data.detail || "Login failed", variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Error", description: "Something went wrong", variant: "destructive" });
    }
    setAuthLoading(false);
  };

  // Send OTP
  const handleSendOTP = async () => {
    if (!authForm.phone || !isValidIndianPhone(authForm.phone)) {
      setErrors({ phone: 'Please enter a valid 10-digit Indian mobile number' });
      return;
    }
    
    setAuthLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/test-series/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: authForm.phone, email: authForm.email })
      });
      const data = await res.json();
      
      if (data.success) {
        setOtpSent(true);
        toast({ title: "OTP Sent!", description: `OTP sent to ${authForm.phone}` });
      } else {
        toast({ title: "Error", description: data.detail || "Failed to send OTP", variant: "destructive" });
      }
    } catch (err) {
      // For demo, simulate OTP sent
      setOtpSent(true);
      toast({ title: "OTP Sent!", description: `Demo OTP: 123456` });
    }
    setAuthLoading(false);
  };

  // Verify OTP
  const handleVerifyOTP = async () => {
    if (!authForm.otp) {
      setErrors({ otp: 'Please enter the OTP' });
      return;
    }
    
    setAuthLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/test-series/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phone: authForm.phone, 
          email: authForm.email,
          otp: authForm.otp,
          name: authForm.name
        })
      });
      const data = await res.json();
      
      if (data.success) {
        setUser(data.user);
        setIsLoggedIn(true);
        localStorage.setItem('test_series_user', JSON.stringify(data.user));
        localStorage.setItem('test_series_user_id', data.user.id);
        setShowAuthModal(false);
        toast({ title: "Welcome!", description: "Logged in successfully" });
        fetchData();
      } else {
        toast({ title: "Error", description: data.detail || "Invalid OTP", variant: "destructive" });
      }
    } catch (err) {
      // For demo, accept 123456
      if (authForm.otp === '123456') {
        const demoUser = {
          id: `user_${Date.now()}`,
          name: authForm.name || 'Student',
          email: authForm.email,
          phone: authForm.phone
        };
        setUser(demoUser);
        setIsLoggedIn(true);
        localStorage.setItem('test_series_user', JSON.stringify(demoUser));
        localStorage.setItem('test_series_user_id', demoUser.id);
        setShowAuthModal(false);
        toast({ title: "Welcome!", description: "Logged in successfully" });
        fetchData();
      } else {
        toast({ title: "Error", description: "Invalid OTP", variant: "destructive" });
      }
    }
    setAuthLoading(false);
  };

  // Logout
  const handleLogout = () => {
    setUser(null);
    setIsLoggedIn(false);
    setUserSubscription(null);
    localStorage.removeItem('test_series_user');
    localStorage.removeItem('test_series_user_id');
    toast({ title: "Logged out", description: "See you soon!" });
  };

  // Start test (requires login)
  const startTest = async (test) => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    
    if (test.is_locked) {
      setActiveTab('plans');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/test-series/test/${test.id}?user_id=${user.id}`);
      const data = await res.json();
      
      if (data.success) {
        setTestQuestions(data.questions);
        setSelectedTest(data.test);
        
        const startRes = await fetch(`${API_URL}/api/test-series/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ test_id: test.id, user_id: user.id })
        });
        const startData = await startRes.json();
        
        if (startData.success) {
          setAttemptId(startData.attempt_id);
          setTimeLeft(test.duration_minutes * 60);
          setTestStarted(true);
          setAnswers({});
          setCurrentQuestion(0);
        }
      }
    } catch (err) {
      console.error('Error starting test:', err);
      toast({ title: "Error", description: "Failed to start test", variant: "destructive" });
    }
  };

  // Timer effect
  useEffect(() => {
    if (testStarted && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            submitTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [testStarted, timeLeft]);

  const selectAnswer = (questionId, optionIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: optionIndex
    }));
  };

  const submitTest = async () => {
    const answersArray = Object.entries(answers).map(([qId, opt]) => ({
      question_id: qId,
      selected_option: opt
    }));

    try {
      const res = await fetch(`${API_URL}/api/test-series/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attempt_id: attemptId,
          answers: answersArray,
          time_taken_seconds: (selectedTest.duration_minutes * 60) - timeLeft
        })
      });
      const data = await res.json();
      
      if (data.success) {
        setTestResult(data);
        setTestCompleted(true);
        setTestStarted(false);
        
        const lbRes = await fetch(`${API_URL}/api/test-series/leaderboard/${selectedTest.id}`);
        const lbData = await lbRes.json();
        if (lbData.success) {
          setLeaderboard(lbData.leaderboard);
        }
      }
    } catch (err) {
      console.error('Error submitting test:', err);
    }
  };

  // Handle plan purchase
  const handlePurchasePlan = async (plan) => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }

    if (plan.price === 0) {
      // Free plan - enroll directly
      try {
        const res = await fetch(`${API_URL}/api/test-series/enroll`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: user.id, plan_id: plan.id })
        });
        const data = await res.json();
        
        if (data.success) {
          setUserSubscription(data.subscription);
          toast({ title: "Success!", description: "Enrolled in free plan" });
          fetchData();
        }
      } catch (err) {
        toast({ title: "Error", description: "Enrollment failed", variant: "destructive" });
      }
    } else {
      // Paid plan - show payment modal
      setSelectedPlan(plan);
      setShowPaymentModal(true);
    }
  };

  // Process payment
  const processPayment = async (paymentMethod) => {
    setPaymentLoading(true);
    
    try {
      // Create payment record
      const res = await fetch(`${API_URL}/api/test-series/create-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          plan_id: selectedPlan.id,
          amount: selectedPlan.price,
          payment_method: paymentMethod
        })
      });
      const data = await res.json();
      
      if (data.success && data.payment_link) {
        // Redirect to payment
        window.location.href = data.payment_link;
      } else {
        // For demo, simulate successful payment
        const enrollRes = await fetch(`${API_URL}/api/test-series/enroll`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            user_id: user.id, 
            plan_id: selectedPlan.id,
            payment_id: `demo_${Date.now()}`
          })
        });
        const enrollData = await enrollRes.json();
        
        if (enrollData.success) {
          setUserSubscription(enrollData.subscription);
          setShowPaymentModal(false);
          toast({ title: "Payment Successful!", description: `You're now subscribed to ${selectedPlan.name}` });
          fetchData();
        }
      }
    } catch (err) {
      toast({ title: "Error", description: "Payment failed", variant: "destructive" });
    }
    
    setPaymentLoading(false);
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading Test Series...</p>
        </div>
      </div>
    );
  }

  // Test Taking UI
  if (testStarted && selectedTest && testQuestions.length > 0) {
    const question = testQuestions[currentQuestion];
    
    return (
      <div className="min-h-screen bg-slate-100">
        {/* Header */}
        <div className="bg-white shadow-sm sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-slate-800">{selectedTest.title}</h2>
              <p className="text-sm text-slate-500">Question {currentQuestion + 1} of {testQuestions.length}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${timeLeft < 300 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                <Timer className="w-5 h-5" />
                <span className="font-mono font-bold text-lg">{formatTime(timeLeft)}</span>
              </div>
              <Button onClick={submitTest} variant="destructive">
                Submit Test
              </Button>
            </div>
          </div>
          <Progress value={(currentQuestion + 1) / testQuestions.length * 100} className="h-1" />
        </div>

        {/* Question */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={question.difficulty === 'easy' ? 'secondary' : question.difficulty === 'hard' ? 'destructive' : 'default'}>
                  {question.difficulty}
                </Badge>
                <Badge variant="outline">{question.subject}</Badge>
                <Badge variant="outline">{question.topic}</Badge>
              </div>
              <CardTitle className="text-lg leading-relaxed">{question.question}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {question.options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => selectAnswer(question.id, idx)}
                    className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                      answers[question.id] === idx 
                        ? 'border-blue-500 bg-blue-50 text-blue-800' 
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <span className="font-semibold mr-3">
                      {String.fromCharCode(65 + idx)}.
                    </span>
                    {option}
                  </button>
                ))}
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button 
                variant="outline" 
                onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
                disabled={currentQuestion === 0}
              >
                Previous
              </Button>
              <div className="flex gap-1 flex-wrap max-w-md justify-center">
                {testQuestions.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentQuestion(idx)}
                    className={`w-8 h-8 rounded text-xs font-medium ${
                      idx === currentQuestion 
                        ? 'bg-blue-600 text-white' 
                        : answers[testQuestions[idx]?.id] !== undefined
                          ? 'bg-green-100 text-green-800 border border-green-300'
                          : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
              <Button 
                onClick={() => setCurrentQuestion(prev => Math.min(testQuestions.length - 1, prev + 1))}
                disabled={currentQuestion === testQuestions.length - 1}
              >
                Next
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // Test Result UI
  if (testCompleted && testResult) {
    return (
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="shadow-xl">
            <CardHeader className="text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg">
              <Trophy className="w-16 h-16 mx-auto mb-4" />
              <CardTitle className="text-2xl">Test Completed!</CardTitle>
              <CardDescription className="text-white/90">{selectedTest?.title}</CardDescription>
            </CardHeader>
            <CardContent className="p-8">
              <div className="grid grid-cols-4 gap-6 mb-8">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">{testResult.score.correct}</p>
                  <p className="text-sm text-green-700">Correct</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-3xl font-bold text-red-600">{testResult.score.wrong}</p>
                  <p className="text-sm text-red-700">Wrong</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">{testResult.score.total_marks}</p>
                  <p className="text-sm text-blue-700">Score</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-3xl font-bold text-purple-600">{testResult.score.accuracy}%</p>
                  <p className="text-sm text-purple-700">Accuracy</p>
                </div>
              </div>

              {leaderboard.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-500" /> Leaderboard
                  </h3>
                  <div className="space-y-2">
                    {leaderboard.slice(0, 5).map((entry, idx) => (
                      <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${
                        entry.user_id === user?.id ? 'bg-blue-100 border-2 border-blue-500' : 'bg-slate-50'
                      }`}>
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                            idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                            idx === 1 ? 'bg-slate-300 text-slate-700' :
                            idx === 2 ? 'bg-orange-300 text-orange-800' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {entry.rank}
                          </span>
                          <span className="font-medium">{entry.user_name}</span>
                          {entry.user_id === user?.id && <Badge>You</Badge>}
                        </div>
                        <span className="font-semibold">{entry.score} pts</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Solutions Section */}
              {testResult.show_solutions && testResult.detailed_results && testResult.detailed_results.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold mb-4 flex items-center gap-2 text-lg">
                    <BookOpen className="w-5 h-5 text-blue-600" /> Detailed Solutions
                  </h3>
                  <div className="space-y-4 max-h-96 overflow-y-auto border rounded-lg p-4">
                    {testResult.detailed_results.map((result, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border-2 ${
                        result.is_correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-start justify-between mb-2">
                          <span className="font-semibold text-slate-700">Q{idx + 1}.</span>
                          <Badge variant={result.is_correct ? 'default' : 'destructive'}>
                            {result.is_correct ? `+${result.marks_obtained}` : result.marks_obtained}
                          </Badge>
                        </div>
                        <p className="text-slate-800 mb-3">{result.question_text}</p>
                        
                        {/* Options */}
                        <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                          {result.options?.map((opt, optIdx) => (
                            <div key={optIdx} className={`p-2 rounded ${
                              optIdx === result.correct ? 'bg-green-200 font-medium' : 
                              optIdx === result.selected && !result.is_correct ? 'bg-red-200' : 'bg-white'
                            }`}>
                              {String.fromCharCode(65 + optIdx)}. {opt}
                              {optIdx === result.correct && <CheckCircle className="inline w-4 h-4 ml-1 text-green-600" />}
                              {optIdx === result.selected && !result.is_correct && <XCircle className="inline w-4 h-4 ml-1 text-red-600" />}
                            </div>
                          ))}
                        </div>
                        
                        {/* Your Answer vs Correct */}
                        <div className="text-sm mb-2">
                          <span className="text-slate-600">Your answer: </span>
                          <span className={result.is_correct ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                            {result.selected !== undefined ? String.fromCharCode(65 + result.selected) : 'Not attempted'}
                          </span>
                          {!result.is_correct && (
                            <>
                              <span className="text-slate-600"> | Correct: </span>
                              <span className="text-green-600 font-medium">{String.fromCharCode(65 + result.correct)}</span>
                            </>
                          )}
                        </div>
                        
                        {/* Solution */}
                        {result.solution && (
                          <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                            <p className="text-sm font-medium text-blue-800 mb-1">Solution:</p>
                            <p className="text-sm text-blue-700">{result.solution}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-4 justify-center">
                <Button variant="outline" onClick={() => {
                  setTestCompleted(false);
                  setSelectedTest(null);
                  setTestResult(null);
                }}>
                  Back to Tests
                </Button>
                <Button onClick={() => window.location.reload()}>
                  Take Another Test
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main Test Series UI
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-10 h-10" />
                <h1 className="text-3xl font-bold">JEE Ranker Booster Series</h1>
              </div>
              <p className="text-xl text-white/90 mb-6">
                Boost your rank with 500+ practice questions, full mock tests, and detailed analytics
              </p>
              <div className="flex gap-6 text-sm">
                <span className="flex items-center gap-2"><BookOpen className="w-5 h-5" /> 500+ Questions</span>
                <span className="flex items-center gap-2"><Target className="w-5 h-5" /> 20+ Mock Tests</span>
                <span className="flex items-center gap-2"><BarChart3 className="w-5 h-5" /> AI Analytics</span>
                <span className="flex items-center gap-2"><Users className="w-5 h-5" /> All India Ranking</span>
              </div>
            </div>
            
            {/* User info / Login button */}
            <div className="text-right">
              {isLoggedIn ? (
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="font-medium">👋 Hello, {user?.name}</p>
                  <p className="text-sm text-white/70">{user?.email}</p>
                  {userSubscription && (
                    <Badge className="mt-2 bg-yellow-400 text-yellow-900">
                      {userSubscription.plan_name} Plan
                    </Badge>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleLogout}
                    className="mt-2 text-white/70 hover:text-white"
                  >
                    Logout
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={() => setShowAuthModal(true)}
                  className="bg-white text-blue-600 hover:bg-blue-50"
                >
                  Sign In to Start
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 -mt-6">
        <div className="bg-white rounded-lg shadow-lg p-2 flex gap-2">
          {['tests', 'plans', 'my-progress'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                activeTab === tab 
                  ? 'bg-blue-600 text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {tab === 'tests' && 'Available Tests'}
              {tab === 'plans' && 'Subscription Plans'}
              {tab === 'my-progress' && 'My Progress'}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Tests Tab */}
        {activeTab === 'tests' && (
          <div>
            {!isLoggedIn && (
              <Card className="mb-6 bg-yellow-50 border-yellow-200">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-yellow-600" />
                    <span className="text-yellow-800">Sign in to track your progress and access all features</span>
                  </div>
                  <Button onClick={() => setShowAuthModal(true)} size="sm">
                    Sign In
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {/* Discussion Forum CTA */}
            <Card className="mb-6 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white border-0 overflow-hidden relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                    <MessageSquare className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Join the Discussion Forum</h3>
                    <p className="text-white/80 text-sm">Ask doubts, share tips, and learn from the community</p>
                  </div>
                </div>
                <Button 
                  variant="secondary" 
                  className="bg-white text-purple-700 hover:bg-purple-50"
                  onClick={() => navigate('/forum')}
                  data-testid="forum-cta-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Visit Forum
                </Button>
              </CardContent>
            </Card>
            
            {/* Free Tests */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-slate-700 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-green-500" /> Free Tests
              </h3>
              {tests.filter(t => t.is_free).length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tests.filter(t => t.is_free).map(test => (
                    <Card key={test.id} className="hover:shadow-lg transition-shadow">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary" className="bg-green-100 text-green-700">Free</Badge>
                          <Badge variant="outline">{test.subject}</Badge>
                        </div>
                        <CardTitle className="text-lg mt-2">{test.title}</CardTitle>
                        <CardDescription>{test.description}</CardDescription>
                      </CardHeader>
                      <CardContent className="pb-2">
                        <div className="flex gap-4 text-sm text-slate-600">
                          <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {test.duration_minutes} min</span>
                          <span className="flex items-center gap-1"><BookOpen className="w-4 h-4" /> {test.total_questions} Qs</span>
                        </div>
                      </CardContent>
                      <CardFooter>
                        <Button onClick={() => startTest(test)} className="w-full">
                          <Play className="w-4 h-4 mr-2" /> {isLoggedIn ? 'Start Test' : 'Sign In to Start'}
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="bg-slate-50 border-dashed">
                  <CardContent className="p-8 text-center">
                    <Star className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">Free tests coming soon! Check back later.</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Paid Tests */}
            <div>
              <h3 className="text-lg font-medium text-slate-700 mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" /> Premium Tests
              </h3>
              {tests.filter(t => !t.is_free).length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tests.filter(t => !t.is_free).map(test => (
                    <Card key={test.id} className={`hover:shadow-lg transition-shadow ${test.is_locked ? 'opacity-75' : ''}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <Badge variant={test.pack === 'premium' ? 'default' : 'secondary'} 
                                 className={test.pack === 'premium' ? 'bg-purple-600' : test.pack === 'pro' ? 'bg-blue-600 text-white' : ''}>
                            {test.pack?.toUpperCase()}
                          </Badge>
                          {test.is_locked && <Lock className="w-4 h-4 text-slate-400" />}
                        </div>
                        <CardTitle className="text-lg mt-2">{test.title}</CardTitle>
                        <CardDescription>{test.description}</CardDescription>
                      </CardHeader>
                      <CardContent className="pb-2">
                        <div className="flex gap-4 text-sm text-slate-600">
                          <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {test.duration_minutes} min</span>
                          <span className="flex items-center gap-1"><BookOpen className="w-4 h-4" /> {test.total_questions} Qs</span>
                        </div>
                      </CardContent>
                      <CardFooter>
                        <Button 
                          onClick={() => startTest(test)} 
                          className="w-full"
                          variant={test.is_locked ? 'outline' : 'default'}
                        >
                          {test.is_locked ? (
                            <><Lock className="w-4 h-4 mr-2" /> Unlock</>
                          ) : (
                            <><Play className="w-4 h-4 mr-2" /> Start Test</>
                          )}
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="bg-slate-50 border-dashed">
                  <CardContent className="p-8 text-center">
                    <Trophy className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">Premium tests coming soon! Subscribe to get notified.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Plans Tab */}
        {activeTab === 'plans' && (
          <div>
            <h2 className="text-xl font-semibold text-slate-800 mb-6 text-center">Choose Your Plan</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {plans.map(plan => (
                <Card key={plan.id} className={`relative ${plan.id === 'pro' ? 'border-2 border-blue-500 shadow-xl' : ''}`}>
                  {plan.id === 'pro' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-blue-600">Most Popular</Badge>
                    </div>
                  )}
                  <CardHeader className="text-center">
                    <CardTitle className="text-xl">{plan.name}</CardTitle>
                    <div className="mt-2">
                      {plan.original_price && (
                        <span className="text-sm text-slate-400 line-through mr-2">₹{plan.original_price}</span>
                      )}
                      <span className="text-3xl font-bold text-slate-800">
                        {plan.price === 0 ? 'Free' : `₹${plan.price}`}
                      </span>
                      {plan.price > 0 && <span className="text-sm text-slate-500">/{plan.duration_days} days</span>}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardFooter>
                    <Button 
                      onClick={() => handlePurchasePlan(plan)} 
                      className="w-full"
                      variant={plan.id === 'pro' ? 'default' : 'outline'}
                      disabled={userSubscription?.plan_id === plan.id}
                    >
                      {userSubscription?.plan_id === plan.id ? 'Current Plan' : plan.price === 0 ? 'Start Free' : 'Subscribe'}
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* My Progress Tab */}
        {activeTab === 'my-progress' && (
          <div>
            <h2 className="text-xl font-semibold text-slate-800 mb-6">My Progress</h2>
            
            {!isLoggedIn ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Lock className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500 mb-4">Sign in to view your progress</p>
                  <Button onClick={() => setShowAuthModal(true)}>
                    Sign In
                  </Button>
                </CardContent>
              </Card>
            ) : myAttempts.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No tests attempted yet</p>
                  <Button onClick={() => setActiveTab('tests')} className="mt-4">
                    Start Your First Test
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myAttempts.map(attempt => (
                  <Card key={attempt.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{attempt.test_id}</h4>
                          <p className="text-sm text-slate-500">
                            {new Date(attempt.started_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          {attempt.status === 'completed' ? (
                            <>
                              <p className="font-bold text-lg text-blue-600">{attempt.score?.total_marks} pts</p>
                              <p className="text-sm text-slate-500">{attempt.score?.accuracy}% accuracy</p>
                            </>
                          ) : (
                            <Badge variant="secondary">In Progress</Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Auth Modal */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {authMode === 'signup' ? 'Create Account' : authMode === 'otp' ? 'OTP Login' : 'Welcome Back'}
            </DialogTitle>
            <DialogDescription>
              {authMode === 'signup' 
                ? 'Sign up to track your progress and access all features' 
                : 'Sign in to continue your learning journey'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {authMode === 'signup' && (
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  placeholder="Enter your name"
                  value={authForm.name}
                  onChange={(e) => setAuthForm({...authForm, name: e.target.value})}
                />
                {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Email Address *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  type="email"
                  placeholder="your@email.com"
                  className="pl-10"
                  value={authForm.email}
                  onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                />
              </div>
              {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
            </div>
            
            <div className="space-y-2">
              <Label>Mobile Number * (Indian)</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  type="tel"
                  placeholder="9876543210"
                  className="pl-10"
                  value={authForm.phone}
                  onChange={(e) => setAuthForm({...authForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                  maxLength={10}
                />
              </div>
              {errors.phone && <p className="text-sm text-red-500">{errors.phone}</p>}
              <p className="text-xs text-slate-500">10-digit mobile number starting with 6, 7, 8, or 9</p>
            </div>
            
            {authMode === 'otp' ? (
              <>
                {!otpSent ? (
                  <Button onClick={handleSendOTP} className="w-full" disabled={authLoading}>
                    {authLoading ? 'Sending...' : 'Send OTP'}
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Label>Enter OTP</Label>
                    <Input
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={authForm.otp}
                      onChange={(e) => setAuthForm({...authForm, otp: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      maxLength={6}
                    />
                    {errors.otp && <p className="text-sm text-red-500">{errors.otp}</p>}
                    <Button onClick={handleVerifyOTP} className="w-full" disabled={authLoading}>
                      {authLoading ? 'Verifying...' : 'Verify & Login'}
                    </Button>
                    <Button variant="link" onClick={handleSendOTP} className="w-full">
                      Resend OTP
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter password"
                      value={authForm.password}
                      onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
                </div>
                
                <Button 
                  onClick={authMode === 'signup' ? handleSignup : handleLogin} 
                  className="w-full" 
                  disabled={authLoading}
                >
                  {authLoading ? 'Please wait...' : authMode === 'signup' ? 'Create Account' : 'Login'}
                </Button>
              </>
            )}
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">Or</span>
              </div>
            </div>
            
            <div className="flex gap-2">
              {authMode !== 'otp' && (
                <Button 
                  variant="outline" 
                  onClick={() => { setAuthMode('otp'); setOtpSent(false); }}
                  className="flex-1"
                >
                  <Phone className="w-4 h-4 mr-2" /> Login with OTP
                </Button>
              )}
              {authMode === 'otp' && (
                <Button 
                  variant="outline" 
                  onClick={() => setAuthMode('login')}
                  className="flex-1"
                >
                  Login with Password
                </Button>
              )}
            </div>
            
            <p className="text-center text-sm text-slate-500">
              {authMode === 'signup' ? (
                <>Already have an account? <button onClick={() => setAuthMode('login')} className="text-blue-600 hover:underline">Login</button></>
              ) : (
                <>Don't have an account? <button onClick={() => setAuthMode('signup')} className="text-blue-600 hover:underline">Sign Up</button></>
              )}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Payment</DialogTitle>
            <DialogDescription>
              Subscribe to {selectedPlan?.name} plan for ₹{selectedPlan?.price}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Card className="mb-4 bg-slate-50">
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{selectedPlan?.name} Plan</span>
                  <span className="text-xl font-bold">₹{selectedPlan?.price}</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">Valid for {selectedPlan?.duration_days} days</p>
              </CardContent>
            </Card>
            
            <div className="space-y-3">
              <Button 
                onClick={() => processPayment('razorpay')} 
                className="w-full"
                disabled={paymentLoading}
              >
                {paymentLoading ? 'Processing...' : 'Pay with Razorpay'}
              </Button>
              <Button 
                onClick={() => processPayment('upi')} 
                variant="outline"
                className="w-full"
                disabled={paymentLoading}
              >
                Pay with UPI
              </Button>
              <Button 
                onClick={() => processPayment('card')} 
                variant="outline"
                className="w-full"
                disabled={paymentLoading}
              >
                Pay with Card/Net Banking
              </Button>
            </div>
            
            <p className="text-xs text-center text-slate-500 mt-4">
              Secure payment powered by Razorpay. Your payment info is encrypted.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestSeries;
